import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Mail, MailCheck, ArrowRight, Loader2, Check, AlertCircle } from "lucide-react";

/**
 * Pricing-First Phase B — Stripe success landing.
 *
 * The webhook (server-side) is the source of truth for account creation and
 * setup-email delivery. This page just confirms the payment so the UX feels
 * complete and tells the buyer to check their inbox for the magic link.
 *
 * We deliberately DO NOT call /api/pro/provision here anymore — that path
 * stays alive for legacy use only.
 */
export default function ProPaymentSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | success | expired | timeout
  const hasPolled = useRef(false);

  // Resend setup-link state (Phase B self-service).
  const [resendState, setResendState] = useState("idle"); // idle | sending | sent | activated | error | rate_limited
  const [resendMessage, setResendMessage] = useState("");

  const handleResend = async () => {
    if (!sessionId || resendState === "sending") return;
    setResendState("sending");
    setResendMessage("");
    try {
      const { data } = await api.post("/pro/setup-token/resend", { session_id: sessionId });
      if (data?.status === "sent") {
        setResendState("sent");
      } else {
        // Neutral "not_found" → still tell the user something useful but
        // don't leak whether their email is on file.
        setResendState("sent");
      }
    } catch (err) {
      const code = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (code === 409) {
        setResendState("activated");
        setResendMessage(detail || "Account already activated. Please log in.");
      } else if (code === 429) {
        setResendState("rate_limited");
        setResendMessage(detail || "Too many attempts. Please try again in a few minutes.");
      } else {
        setResendState("error");
        setResendMessage(detail || "Could not resend. Please try again in a moment.");
      }
    }
  };

  useEffect(() => {
    if (!sessionId || hasPolled.current) return;
    hasPolled.current = true;

    let attempts = 0;
    const maxAttempts = 12;
    const pollInterval = 2000;

    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/pro-status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("success");
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
      } catch { /* keep polling */ }

      attempts++;
      if (attempts < maxAttempts) {
        setTimeout(poll, pollInterval);
      } else {
        // Even on timeout we surface success — webhook is async and the user
        // already received the email. Avoid scaring buyers off after Stripe
        // returns.
        setStatus("success");
      }
    };

    poll();
  }, [sessionId]);

  if (status === "polling") {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center" data-testid="pro-payment-polling">
        <div className="text-center space-y-4">
          <div className="w-10 h-10 border-2 border-zinc-900 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-sm text-zinc-500">Confirming your payment…</p>
        </div>
      </div>
    );
  }

  if (status === "expired") {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4" data-testid="pro-payment-failed">
        <div className="max-w-sm text-center space-y-5">
          <h1 className="text-xl font-semibold text-[#111111]">Session expired</h1>
          <p className="text-sm text-zinc-500">
            Your payment session has expired. Please try again.
          </p>
          <Button onClick={() => navigate("/pricing")} className="w-full h-10 bg-zinc-900 hover:bg-zinc-800 text-white">
            Back to pricing
          </Button>
        </div>
      </div>
    );
  }

  // success
  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-4" data-testid="pro-payment-success">
      <div className="max-w-md text-center space-y-6">
        <div className="w-20 h-20 mx-auto rounded-2xl bg-emerald-50 flex items-center justify-center">
          <MailCheck size={32} className="text-emerald-600" />
        </div>
        <div className="space-y-2">
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[#111111]">
            Check your inbox
          </h1>
          <p className="text-sm text-zinc-500 leading-relaxed">
            Payment confirmed. We've emailed you a secure link to finish setting up your PRO account —
            choose your username and password to unlock your inbox.
          </p>
        </div>

        <div className="rounded-lg bg-zinc-50 border border-zinc-200 px-4 py-3 text-left flex gap-3 items-start">
          <Mail size={18} className="text-zinc-500 shrink-0 mt-0.5" />
          <div className="text-xs text-zinc-600 leading-relaxed">
            The link is valid for <strong className="text-zinc-800">24 hours</strong> and works only once.
            Can't find it? Check your spam folder.
          </div>
        </div>

        {/* Resend self-service — small, inline, no extra UI surface */}
        <div className="space-y-2" data-testid="pro-payment-resend-section">
          {resendState === "sent" ? (
            <div
              className="flex items-center justify-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2.5"
              data-testid="pro-payment-resend-sent"
            >
              <Check size={14} className="shrink-0" />
              <span>New link sent. Please check your email.</span>
            </div>
          ) : resendState === "activated" ? (
            <div
              className="flex items-center justify-center gap-2 text-xs text-zinc-700 bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2.5"
              data-testid="pro-payment-resend-activated"
            >
              <AlertCircle size={14} className="shrink-0 text-zinc-500" />
              <span>{resendMessage || "Account already activated."}</span>
            </div>
          ) : resendState === "rate_limited" ? (
            <div
              className="flex items-center justify-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2.5"
              data-testid="pro-payment-resend-rate-limited"
            >
              <AlertCircle size={14} className="shrink-0" />
              <span>{resendMessage}</span>
            </div>
          ) : resendState === "error" ? (
            <div
              className="flex items-center justify-center gap-2 text-xs text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2.5"
              data-testid="pro-payment-resend-error"
            >
              <AlertCircle size={14} className="shrink-0" />
              <span>{resendMessage}</span>
            </div>
          ) : (
            <button
              type="button"
              onClick={handleResend}
              disabled={resendState === "sending" || !sessionId}
              className="w-full text-xs text-zinc-600 hover:text-zinc-900 underline-offset-4 hover:underline transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-1.5 py-2"
              data-testid="pro-payment-resend-btn"
            >
              {resendState === "sending"
                ? (<><Loader2 size={12} className="animate-spin" /> Sending…</>)
                : "Didn't get the email? Resend setup link"}
            </button>
          )}
        </div>

        <Button
          onClick={() => navigate("/pro/login")}
          variant="outline"
          className="w-full h-10"
          data-testid="pro-payment-go-login"
        >
          Already activated? Log in <ArrowRight size={14} className="ml-1.5" />
        </Button>
      </div>
    </div>
  );
}
